/**
 * @file services/fulfillment.ts
 * @description Service for handling fulfillment operations (model supports)
 * 
 * This service provides hooks for managing fulfillment objects which represent 
 * model supports in the 409A operations workflow. Model supports are required
 * before certain operations can be performed.
 * 
 * Key Features:
 * - Fetch fulfillment list for a company
 * - Create new model supports (fulfillments)
 * - Check if a company has existing model support
 * - Proper error handling and loading states
 * 
 * Usage:
 * ```tsx
 * const { data: fulfillments, isLoading } = useFulfillmentList(companyId);
 * const createFulfillment = useCreateFulfillment(companyId);
 * const hasSupport = hasModelSupport(fulfillments);
 * ```
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';

// Query keys for fulfillment operations
export const FulfillmentQueryKeys = {
  FULFILLMENT_LIST: 'fulfillment-list',
  FULFILLMENT_DETAIL: 'fulfillment-detail',
} as const;

/**
 * Hook to get fulfillment list for a company
 */
export const useFulfillmentList = (companyId: string | undefined, page?: number) => useQuery({
  queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId, page],
  queryFn: () => ApiService.apiDashboardCompanyOperationsFulfillmentList(companyId!, page),
  enabled: !!companyId,
  staleTime: 5 * 60 * 1000, // 5 minutes
  retry: false,
  refetchOnWindowFocus: false,
});

/**
 * Hook to create a new fulfillment (model support)
 * 
 * @param companyId - The legacy company ID (not Attio valued entity ID)
 */
export const useCreateFulfillment = (companyId: string | undefined) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (fulfillmentData: Partial<Fulfillment>) => {
      if (!companyId) {
        throw new Error('Company ID is required to create fulfillment');
      }
      return ApiService.apiDashboardCompanyOperationsFulfillmentCreate(
        companyId,
        fulfillmentData as Fulfillment
      );
    },
    onSuccess: data => {
      // Invalidate and refetch fulfillment list
      queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId],
      });
      
      enqueueSnackbar('Model support created successfully', { 
        variant: 'success' 
      });
    },
    onError: (error: any) => {
      console.error('Error creating fulfillment:', error);
      enqueueSnackbar(
        error?.message || 'Failed to create model support. Please try again.',
        { variant: 'error' }
      );
    },
  });
};

/**
 * Helper function to get the latest fulfillment for a company
 */
export const getLatestFulfillment = (fulfillmentData?: { results: Fulfillment[] }): Fulfillment | null => {
  if (!fulfillmentData?.results || fulfillmentData.results.length === 0) {
    return null;
  }
  
  // Return the first fulfillment (assuming API returns them sorted by creation date desc)
  return fulfillmentData.results[0];
};
